import React, { useCallback, useEffect } from 'react';
import { Support } from '../../hooks/Support';
import Hls from 'hls.js';

export type VolumeProps = {
  videoContainer: React.RefObject<HTMLDivElement>;
  enabled: boolean;
  hls: Hls;
  code: string;
};

const Volume = ({ videoContainer, enabled, hls, code }: VolumeProps) => {
  const [volume, setVolume] = React.useState<number>(1);

  const onVolumeClick = useCallback((open: boolean) => {
    const rootElement = document.querySelector(`[data-code="${code}"]`) as HTMLDivElement;

    const configurationButton = rootElement.querySelector('.smartplayer__configuration') as HTMLButtonElement;
    configurationButton.classList.remove('smartplayer__configuration--opened');

    const aiButton = rootElement.querySelector('.smartplayer__ai') as HTMLButtonElement;
    aiButton.classList.remove('smartplayer__ai--opened');

    if (open) {
      rootElement.querySelector('.smartplayer__volume')?.classList.add('smartplayer__volume--opened');
      // rootElement.querySelector('.smartplayer__footer')?.classList.add('smartplayer__footer--dropdown');
    } else {
      rootElement.querySelector('.smartplayer__volume')?.classList.remove('smartplayer__volume--opened');
      // rootElement.querySelector('.smartplayer__footer')?.classList.remove('smartplayer__footer--dropdown');
    }

  }, [code]);

  const onVolumeChange = useCallback((value: string | number) => {
    let volume = Number(value);
    const max = 1;
    const min = 0;

    // Maximum is volumeMax
    if (volume > max) {
      volume = max;
    }
    // Minimum is volumeMin
    if (volume < min) {
      volume = min;
    }

    if (!hls || !hls.media) {
      return;
    }

    // Set the player volume
    hls.media.volume = volume;

    // If muted, and we're increasing volume manually, reset muted state
    if (hls.media.muted && volume > 0) {
      videoContainer?.current?.classList.remove('smartplayer--autoplaying');
      hls.media.muted = false;
    }

    setVolume(volume);
  }, [hls]);

  const onVolumeChangeFromEvent = useCallback((event: any) => {
    if (!hls || !hls.media) {
      return;
    }

    if (hls.media.muted || hls.media.volume === 0) {
      onVolumeChange(0);
      return;
    }

    onVolumeChange(hls.media.volume);
  }, [hls, hls?.media]);

  useEffect(() => {
    if (!hls || !hls.media) {
      return;
    }

    setVolume(hls.media.volume);

    hls.media.addEventListener("volumechange", onVolumeChangeFromEvent);

    return () => {
      if (!hls || !hls.media) {
        return;
      }

      hls.media?.removeEventListener("volumechange", onVolumeChangeFromEvent);
    }
  }, [hls, hls?.media, onVolumeChangeFromEvent])

  return enabled ? (
    <div className="smartplayer__volume"
      onMouseOver={(event) => {
        onVolumeClick(true);
      }}
      onMouseLeave={(event) => {
        onVolumeClick(false);
      }}
    >
      <button className="smartplayer__bar-button" type="button" onClick={() => onVolumeChange(volume === 1 ? 0 : 1)}>
        <div className="smartplayer__volumeIconBox">
          {
            volume === 0 ?
            <div className="icon icon--muted">
              <svg xmlns="http://www.w3.org/2000/svg" height="32" width="36" viewBox="0 0 576 512" fill="currentColor">
                <path d="M301.1 34.8C312.6 40 320 51.4 320 64V448c0 12.6-7.4 24-18.9 29.2s-25 3.1-34.4-5.3L131.8 352H64c-35.3 0-64-28.7-64-64V224c0-35.3 28.7-64 64-64h67.8L266.7 40.1c9.4-8.4 22.9-10.4 34.4-5.3zM425 167l55 55 55-55c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-55 55 55 55c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0l-55-55-55 55c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l55-55-55-55c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0z"/>
              </svg>
            </div> : null
          }
          {
            volume > 0 && volume <= 0.25 ?
            <div className="icon icon--low">
              <svg xmlns="http://www.w3.org/2000/svg" height="32" width="28" viewBox="0 0 448 512" fill="currentColor">
                <path d="M301.1 34.8C312.6 40 320 51.4 320 64V448c0 12.6-7.4 24-18.9 29.2s-25 3.1-34.4-5.3L131.8 352H64c-35.3 0-64-28.7-64-64V224c0-35.3 28.7-64 64-64h67.8L266.7 40.1c9.4-8.4 22.9-10.4 34.4-5.3zM412.6 181.5C434.1 199.1 448 225.9 448 256s-13.9 56.9-35.4 74.5c-10.3 8.4-25.4 6.8-33.8-3.5s-6.8-25.4 3.5-33.8C393.1 284.4 400 271 400 256s-6.9-28.4-17.7-37.3c-10.3-8.4-11.8-23.5-3.5-33.8s23.5-11.8 33.8-3.5z"/>
              </svg>
            </div> : null
          }
          {
            volume > 0.25 && volume <= 0.75 ?
            <div className="icon icon--medium">
              <svg xmlns="http://www.w3.org/2000/svg" height="32" width="36" viewBox="0 0 576 512" fill="currentColor">
                <path d="M333.1 34.8C344.6 40 352 51.4 352 64V448c0 12.6-7.4 24-18.9 29.2s-25 3.1-34.4-5.3L163.8 352H96c-35.3 0-64-28.7-64-64V224c0-35.3 28.7-64 64-64h67.8L298.7 40.1c9.4-8.4 22.9-10.4 34.4-5.3zm172 72.2c43.2 35.2 70.9 88.9 70.9 149s-27.7 113.8-70.9 149c-10.3 8.4-25.4 6.8-33.8-3.5s-6.8-25.4 3.5-33.8C507.3 341.3 528 301.1 528 256s-20.7-85.3-53.2-111.8c-10.3-8.4-11.8-23.5-3.5-33.8s23.5-11.8 33.8-3.5zm-60.5 74.5C466.1 199.1 480 225.9 480 256s-13.9 56.9-35.4 74.5c-10.3 8.4-25.4 6.8-33.8-3.5s-6.8-25.4 3.5-33.8C425.1 284.4 432 271 432 256s-6.9-28.4-17.7-37.3c-10.3-8.4-11.8-23.5-3.5-33.8s23.5-11.8 33.8-3.5z"/>
              </svg>
            </div> : null
          }
          {
            volume > 0.75 ?
            <div className="icon icon--high">
              <svg xmlns="http://www.w3.org/2000/svg" height="32" width="40" viewBox="0 0 640 512" fill="currentColor">
                <path d="M533.6 32.5C598.5 85.2 640 165.8 640 256s-41.5 170.7-106.4 223.5c-10.3 8.4-25.4 6.8-33.8-3.5s-6.8-25.4 3.5-33.8C557.5 398.2 592 331.2 592 256s-34.5-142.2-88.7-186.3c-10.3-8.4-11.8-23.5-3.5-33.8s23.5-11.8 33.8-3.5zM473.1 107c43.2 35.2 70.9 88.9 70.9 149s-27.7 113.8-70.9 149c-10.3 8.4-25.4 6.8-33.8-3.5s-6.8-25.4 3.5-33.8C475.3 341.3 496 301.1 496 256s-20.7-85.3-53.2-111.8c-10.3-8.4-11.8-23.5-3.5-33.8s23.5-11.8 33.8-3.5zm-60.5 74.5C434.1 199.1 448 225.9 448 256s-13.9 56.9-35.4 74.5c-10.3 8.4-25.4 6.8-33.8-3.5s-6.8-25.4 3.5-33.8C393.1 284.4 400 271 400 256s-6.9-28.4-17.7-37.3c-10.3-8.4-11.8-23.5-3.5-33.8s23.5-11.8 33.8-3.5zM301.1 34.8C312.6 40 320 51.4 320 64V448c0 12.6-7.4 24-18.9 29.2s-25 3.1-34.4-5.3L131.8 352H64c-35.3 0-64-28.7-64-64V224c0-35.3 28.7-64 64-64h67.8L266.7 40.1c9.4-8.4 22.9-10.4 34.4-5.3z"/>
              </svg>
            </div> : null
          }
        </div>
      </button>
      <div className="smartplayer__dropdownContainer">
        <div className="smartplayer__volumeRangeContainer">
          <input type="range" step="0.01" min="0" max="1" value={volume} className="smartplayer__volumeRange"
          style={{ background: `linear-gradient(to right, var(--primary-color) 0%, var(--primary-color) ${volume*100}%, #ffffff33 ${volume*100}%, #ffffff33 100%` }}
          onChange={(event) => {
            const target = event.target as HTMLInputElement;
            target.style.background = `linear-gradient(to right, var(--primary-color) 0%, var(--primary-color) ${volume*100}%, #ffffff33 ${volume*100}%, #ffffff33 100%`;
            onVolumeChange(Number(target.value));
          }} />
        </div>
      </div>
    </div>
  ) : null;
};

export default Volume;
